import { useEffect, useState } from "react";
import { z } from "zod";

const rascunhoSchema = z.object({
  // Versão 3: tudo a partir do serviço (Serviços → Configurar → Confirmar). Rascunho antigo recomeça.
  versao: z.literal(3),
  passo: z.number().int().min(0).max(2),
  /** null = passo ainda não visitado (começa sem nada marcado). */
  servicos: z.array(z.string()).nullable(),
  /** Criados pela pessoa por não achar na base. */
  servicosProprios: z.array(z.object({ nome: z.string(), categoria: z.string() })).default([]),
  materiaisProprios: z.array(z.string()).default([]),
  /** Material próprio → família (rótulo) em que a pessoa o criou; sem entrada, "Criados por você". */
  familiaDosProprios: z.record(z.string(), z.string()).default({}),
  /** Só os serviços que a pessoa configurou; o resto usa os materiais da base. Chave: id ou "proprio:<nome>". */
  materiaisPorServico: z.record(z.string(), z.array(z.string())).default({}),
  /** Material → códigos de escala de cor, como no Catálogo. */
  escalasPorMaterial: z.record(z.string(), z.array(z.string())).default({}),
  /** Só onde a pessoa trocou o que a base diz (dente ou arcada). */
  abrangenciaPorServico: z.record(z.string(), z.enum(["DENTE", "ARCADA"])).default({}),
  /** Serviços que a pessoa já abriu no Configurar (salvando ou não) — ficam verdes. */
  revisados: z.array(z.string()).default([]),
});
export type RascunhoDoComecar = z.infer<typeof rascunhoSchema>;

const VAZIO: RascunhoDoComecar = {
  versao: 3,
  passo: 0,
  servicos: null,
  servicosProprios: [],
  materiaisProprios: [],
  familiaDosProprios: {},
  materiaisPorServico: {},
  escalasPorMaterial: {},
  abrangenciaPorServico: {},
  revisados: [],
};

/** O que o laboratório já escolheu fica no navegador até concluir — recarregar não perde nada. */
export function useRascunhoDoComecar(organizationId: string | undefined) {
  const chave = organizationId ? `comecar:${organizationId}` : null;
  const [rascunho, setRascunho] = useState<RascunhoDoComecar>(() => ler(chave));
  useEffect(() => setRascunho(ler(chave)), [chave]);
  useEffect(() => {
    if (!chave) return;
    try {
      localStorage.setItem(chave, JSON.stringify(rascunho));
    } catch {
      // Navegador sem armazenamento: o onboarding funciona igual, só não sobrevive a recarregar.
    }
  }, [chave, rascunho]);
  const mudar = (parcial: Partial<RascunhoDoComecar>) => setRascunho((r) => ({ ...r, ...parcial }));
  const descartar = () => {
    try {
      if (chave) localStorage.removeItem(chave);
    } catch {
      // idem
    }
  };
  return { rascunho, mudar, descartar };
}

function ler(chave: string | null): RascunhoDoComecar {
  if (!chave) return VAZIO;
  try {
    const salvo = rascunhoSchema.safeParse(JSON.parse(localStorage.getItem(chave) ?? "null"));
    return salvo.success ? salvo.data : VAZIO;
  } catch {
    return VAZIO;
  }
}
